import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import SuccessIcon from '@/assets/icons/success-icon.svg?react';
import Footer from '@/components/common/Footer';
import Header from '@/components/common/Header';
import { useWidgetUIStore } from '@/hooks';
import { WidgetViewState } from '../WidgetOpeningManager';

interface SmsSuccessPageProps {
  onBack?: () => void;
  onClose?: () => void;
}

/**
 * SmsSuccessPage component displays success message after SMS contact form submission
 * Shows checkmark icon, success title, message and close button
 */
const SmsSuccessPage: React.FC<SmsSuccessPageProps> = ({ onBack }) => {
  const { t } = useTranslation('common');
  const { actions: widgetUIActions } = useWidgetUIStore();

  const onClose = () => {
    widgetUIActions.setCurrentView(WidgetViewState.MINIMIZED);
  };

  return (
    <div
      className={classNames(
        'sf:w-full sf:h-full sf:max-w-md sf:mx-auto sf:overflow-y-auto sf:background-gradient-diagonal sf:flex sf:flex-col',
        'sf:sm:rounded-2xl',
      )}
      data-testid="sms-success-page"
    >
      <Header
        onMinimize={onClose}
        onBack={onBack}
        title={t('channels.twilioSms', 'SMS')}
      />

      {/* Main Content - Centered Layout */}
      <div className="sf:flex-1 sf:flex sf:flex-col sf:items-center sf:justify-center sf:px-[50px] sf:text-center sf:overflow-auto">
        {/* Success Checkmark Icon */}
        <div className="sf:mb-5 sf:bg-green-100 sf:rounded-full sf:flex sf:items-center sf:justify-center">
          <SuccessIcon width="44" height="44" />
        </div>

        {/* Success Title */}
        <h1 className="sf:text-headline2 sf:font-semibold sf:mb-1 sf:max-w-sm sf:leading-tight sf:text-gray-900">
          {t('twilioSms.success.title', 'Contact info received')}
        </h1>

        {/* Success Message */}
        <p className="sf:text-body1 sf:text-[#3C3F44] sf:mb-5 sf:max-w-sm sf:leading-relaxed">
          {t(
            'twilioSms.success.message',
            'We will be reaching out via SMS shortly',
          )}
        </p>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="sf:bg-transparent sf:cursor-pointer sf:text-blue-500 sf:py-3 sf:leading-none sf:px-8 sf:border sf:border-blue-500 sf:rounded-full sf:font-semibold sf:transition-all sf:duration-200 sf:transform sf:hover:bg-blue-50 sf:hover:scale-105 sf:active:scale-95 sf:focus:outline-none sf:focus:ring-4 sf:focus:ring-blue-300 sf:focus:ring-opacity-50"
        >
          {t('buttons.close', 'Close')}
        </button>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SmsSuccessPage;
